import { cn } from "@/utils";
import { Message, ToolCall } from "@cohesive/api-client";
import { ErrorBoundary } from "@sentry/react";
import { motion } from "framer-motion";
import {
  Ban,
  Check,
  ChevronRight,
  Loader2,
  TriangleAlert,
  type LucideIcon,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { ToolCallArguments } from "../ToolCall/ToolCallArguments";
import { ToolTitle } from "../ToolCall/ToolCallTitle";
import {
  ToolConfirmationButtons,
  ToolConfirmationStatus,
} from "../ToolCall/ToolConfirmation";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../ui/accordion";
import {
  firstThoughtLine,
  HarnessToolCallItem,
  HarnessToolCallOutcome,
  HarnessToolRunEntry,
  summarizeToolCall,
} from "./harnessRailItems";
import { HarnessToolIcon } from "./HarnessToolIcon";

// Rail rows whose entrance animation has already played, by rail-item key.
// Module scope so a remount (e.g. the whole turn re-keying) doesn't replay the
// pop; the set is read during render but only written in an effect so a
// StrictMode double-render can't mark a row as seen before it commits.
const animatedRailKeys = new Set<string>();

function useEnterOnce(key: string) {
  const [firstMount] = useState(() => !animatedRailKeys.has(key));
  useEffect(() => {
    animatedRailKeys.add(key);
  }, [key]);
  return firstMount;
}

// Match the chain-of-thought step entry (fade + 8px rise at dur-mid /
// design-system ease-out); a remount jumps straight to rest.
function enterProps(firstMount: boolean) {
  return {
    initial: firstMount ? { opacity: 0, y: 8 } : false,
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.2, ease: [0.2, 0, 0, 1] as const },
  };
}

const OUTCOME_GLYPH: Record<
  HarnessToolCallOutcome,
  { Icon: LucideIcon; tone: string }
> = {
  running: { Icon: Loader2, tone: "text-muted-foreground" },
  ok: { Icon: Check, tone: "text-lc-active" },
  error: { Icon: TriangleAlert, tone: "text-lc-blocked" },
  denied: { Icon: Ban, tone: "text-muted-foreground" },
};

/**
 * A run of consecutive calls to one harness tool as a single rail row: the
 * tool, the latest call's gist, and a count that ticks up in place as calls
 * land. Failures never hide — the row carries a failed/denied count in the
 * outcome chip, and expanding it lists every entry in order: each call with
 * its own outcome (arguments behind a click) and any thought the model had
 * between two calls.
 *
 * Only the rail's one live row shimmers (`live`); a run that is still
 * running elsewhere just shows its spinner.
 */
export function HarnessToolCallRun({
  itemKey,
  toolName,
  entries,
  calls,
  live = false,
}: {
  itemKey: string;
  toolName: string;
  entries: HarnessToolRunEntry[];
  calls: HarnessToolCallItem[];
  live?: boolean;
}) {
  const firstMount = useEnterOnce(itemKey);
  const [expanded, setExpanded] = useState(false);

  const latest = calls[calls.length - 1];
  const summary = useMemo(
    () => (latest ? summarizeToolCall(latest.call) : undefined),
    [latest],
  );

  const running = calls.some((c) => c.outcome === "running");
  const failed = calls.filter((c) => c.outcome === "error").length;
  const denied = calls.filter((c) => c.outcome === "denied").length;

  // The run's own outcome: still working beats everything, then the worst
  // thing that happened to any call in it.
  const outcome: HarnessToolCallOutcome = running
    ? "running"
    : failed
      ? "error"
      : denied
        ? "denied"
        : "ok";
  const { Icon: OutcomeIcon, tone } = OUTCOME_GLYPH[outcome];
  const outcomeLabel = [
    failed ? `${failed} failed` : "",
    denied ? `${denied} denied` : "",
  ]
    .filter(Boolean)
    .join(" · ");

  // Restart the shimmer from its origin whenever it starts, so a run that
  // begins mid-sweep doesn't flash (mirrors the legacy tool row). Absent in
  // jsdom.
  useEffect(() => {
    for (const a of document.getAnimations?.() ?? []) {
      if ((a as CSSAnimation).animationName === "shimmer") {
        a.startTime = 0;
      }
    }
  }, [running, live]);

  return (
    <motion.div {...enterProps(firstMount)}>
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
        data-testid="harness-tool-run"
        className="group flex w-full items-center gap-2 px-4 py-2 text-left outline-none"
      >
        {/* Tool glyph, aligned to and masking the activity rail at x≈28px. */}
        <span className="relative z-[1] flex size-6 shrink-0 items-center justify-center bg-background">
          <HarnessToolIcon toolName={toolName} />
        </span>

        <span
          className={cn(
            "min-w-0 flex-1 truncate font-sans text-sm text-n-70",
            running && live && "shimmer text-muted-foreground",
          )}
        >
          {toolName}
          {summary && <span className="ml-2 text-n-50">{summary}</span>}
        </span>

        <span
          className="shrink-0 font-mono text-xs text-n-50"
          data-testid="harness-tool-run-count"
        >
          ×{calls.length}
        </span>

        <span
          className={cn(
            "inline-flex shrink-0 items-center gap-1 text-xs font-medium",
            tone,
          )}
        >
          <OutcomeIcon className={cn("size-3.5", running && "animate-spin")} />
          {outcomeLabel}
        </span>

        <ChevronRight
          className={cn(
            "size-3.5 shrink-0 text-n-40 transition-transform duration-150 ease-out group-hover:text-n-60",
            expanded && "rotate-90",
          )}
        />
      </button>

      {expanded && (
        <div className="mb-2 ml-12 mr-4 rounded-md bg-[color-mix(in_oklab,var(--n-10)_45%,var(--card))] p-1">
          {entries.map((entry) =>
            entry.kind === "call" ? (
              <HarnessToolCallDetailRow
                key={entry.item.key}
                item={entry.item}
              />
            ) : (
              <div
                key={`${entry.message.id}:thought`}
                title={entry.message.thinking_content}
                className="flex items-center gap-2 px-2.5 py-1.5 font-sans text-xs text-n-50"
              >
                <span className="size-1.5 shrink-0 rounded-full bg-n-40" />
                <span className="min-w-0 flex-1 truncate italic">
                  {firstThoughtLine(entry.message.thinking_content ?? "")}
                </span>
              </div>
            ),
          )}
        </div>
      )}
    </motion.div>
  );
}

/** One call inside an expanded run: gist + outcome, arguments on click. */
function HarnessToolCallDetailRow({ item }: { item: HarnessToolCallItem }) {
  const [open, setOpen] = useState(false);
  const summary = summarizeToolCall(item.call);
  const error =
    item.call.result?.error?.message ??
    item.call.function.result?.error?.message;
  const { Icon, tone } = OUTCOME_GLYPH[item.outcome];

  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex w-full items-center gap-2 rounded-sm px-2.5 py-1.5 text-left font-sans text-xs text-n-60 transition-colors hover:bg-card"
      >
        <Icon
          className={cn(
            "size-3 shrink-0",
            tone,
            item.outcome === "running" && "animate-spin",
          )}
        />
        <span className="min-w-0 flex-1 truncate text-n-80">
          {summary ?? item.call.function.name}
        </span>
        {error && (
          <span className="min-w-0 max-w-[50%] truncate text-lc-blocked">
            {error}
          </span>
        )}
      </button>
      {open && (
        <div className="mx-2.5 mb-1.5 rounded-md bg-card p-3">
          <ErrorBoundary>
            <ToolCallArguments
              args={item.call.function.arguments}
              toolCall={item.call}
            />
          </ErrorBoundary>
        </div>
      )}
    </div>
  );
}

// Stable open-state key for the single-item confirmation accordion. Must NOT be
// the tool call's `id`: that id can be re-minted across the delta → terminal →
// persisted lifecycle, and since the row reconciles in place (stable React key)
// rather than remounting, an id-based value would leave the accordion's open
// item pointing at a stale id and snap the card shut.
const CONFIRMATION_ITEM_VALUE = "confirmation";

/**
 * A harness tool call parked on a permission ask. Harness tools run inside the
 * harness's own sandbox, so none of the Cohesive-tool renderers apply (no
 * sandboxed-bash card, no JsonForms schema): the generic arguments view plus
 * the confirmation buttons.
 */
export function HarnessToolConfirmationCall({
  itemKey,
  message,
  toolCall,
}: {
  itemKey: string;
  message: Message;
  toolCall: ToolCall;
}) {
  const firstMount = useEnterOnce(itemKey);
  const [value, onValueChange] = useState<string>();
  const [toolCallArguments, setToolCallArguments] = useState(
    toolCall.function.arguments,
  );

  useEffect(() => {
    setToolCallArguments(toolCall.function.arguments);
  }, [toolCall.function.arguments]);

  const isShimmering =
    toolCall.user_confirmation_status === "confirmed-processing";

  useEffect(() => {
    if (toolCall.user_confirmation_status === "rejected") {
      onValueChange("");
    }
  }, [toolCall.user_confirmation_status]);

  return (
    <motion.div {...enterProps(firstMount)}>
      <Accordion
        type="single"
        collapsible
        className="break-word group px-4 overflow-hidden py-2!"
        defaultValue={CONFIRMATION_ITEM_VALUE}
        value={value}
        onValueChange={onValueChange}
      >
        <AccordionItem value={CONFIRMATION_ITEM_VALUE} className="border-none">
          <AccordionTrigger className="font-normal p-0 text-left">
            <div className="flex items-center justify-between w-full pr-3">
              <ToolTitle call={toolCall} shimmer={isShimmering} />
              <div className="flex justify-end space-x-2">
                <ToolConfirmationStatus
                  toolCall={toolCall}
                  onComplete={() => {
                    onValueChange("");
                  }}
                />
              </div>
            </div>
          </AccordionTrigger>
          <AccordionContent>
            <div className="mt-2 p-4 rounded-xl bg-card">
              <ErrorBoundary>
                <ToolCallArguments
                  args={toolCallArguments}
                  toolCall={toolCall}
                />
              </ErrorBoundary>
            </div>
            <div className="mt-2">
              <ToolConfirmationButtons
                toolCall={toolCall}
                toolCallArguments={toolCallArguments}
                flowSessionId={message.flow_session_id}
                flowMessageId={message.id}
                onComplete={() => {
                  onValueChange("");
                }}
              />
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </motion.div>
  );
}
